import * as PIXI from 'pixi.js';
import Polygon from 'polygon';
import debounce from 'lodash/debounce';
import rubberBandModel from '__editor/panelsEditor/models/rubberBand';
import { cursorPosition } from '__editor/panelsEditor/components/cursor/cursor';
import { dispatch, state } from '__common/store';
import { getNearestGroup } from '../cursor/utils/snapToGridHelper';
import { getPolyMinMaxCoords } from '__common/calculations/polygon';
import { getStage, removeChild } from '__editor/panelsEditor/components/stage/stage';
import { searchCollisionItems } from '__editor/panelsEditor/components/panels/panelsCollisions';
import {
  START,
  RESET_RUBBERBAND_STATE,
  REQUEST_ADD_PANELS,
  SET_TOOLTIP_CONTENT,
  HIDE_CURSOR, SHOW_CURSOR,
  CHANGE_SYSTEM_CURSOR,
  RESET_CURSOR_STATE,
  REMOVE_PANELS,
  SET_MANUAL_EXPOSURE_BULK,
} from 'actions';
import {
  clearRubberBandPreview,
  getPanelsShadowContainer,
  getPreviewPanelsToRemove,
  getPreviewPoints,
  presentPreviewStage,
  previewShadowId,
  applyPreviewPanelSelection,
  setPreviewPoints,
  syncShadowSprites,
  updatePreviewTooltip,
  updateRubberBandOutline,
} from '__editor/panelsEditor/components/rubberBand/rubberBandPreview';
import { get_drawing_direction } from '../panels/utils/populating';
import { draw_double_panels_on_area } from '../panels/utils/draw_double_panels_on_area';
import { drawGftPanelsInTables } from '../panels/utils/drawGftPanelsInTables';
import { drawUlaPanelsInTables } from '../panels/utils/drawUlaPanelsInTables';
import { draw_on_area, DrawPanelFunction } from '../panels/utils/draw_on_area';
import { pairWithSiblings, createPanel } from '../panels/utils/panelsManagment';
import { isRMDT, isGFT, isULA, isAscender } from '__common/constants/products';
import nextUid from '__common/calculations/nextUid';
import { drawAscenderPanelsInTables } from '../panels/utils/drawAscenderPanelsInTables';
import { setPreviewModuleCounts } from '__editor/panelsEditor/components/rubberBand/previewModuleCounts';

const EXPOSURE_RIBBON_INSET_RATIO = 0.2;
const EXPOSURE_COVERAGE_THRESHOLD = 0.6;

export { getPanelsShadowContainer };

export function startRubberBand(x, y) {
  populateWithPanelsShadowDebounced.cancel();
  clearRubberBandPreview();
  dispatch(HIDE_CURSOR());
  dispatch(START(x, y));
  dispatch(CHANGE_SYSTEM_CURSOR('crosshair'));
}

export function resizeRubberBand(startX, startY, x, y) {
  const { rubberBand } = state();
  const points = _calculatePoints(startX, startY, x, y);
  const stage = getStage();
  setPreviewPoints(points);

  if (stage) {
    const ribbonPoints = rubberBand.mode === 'exposure' ? getExposureRibbonPoints(points) : points;
    updateRubberBandOutline(stage, ribbonPoints);
  }

  if (rubberBand.mode === 'draw') {
    populateWithPanelsShadowDebounced();
  } else if (rubberBand.mode === 'remove' || rubberBand.mode === 'exposure') {
    collectPanelsInArea(points);
  }

  presentPreviewStage();
}

export function deployRubberBand() {
  populateWithPanelsShadowDebounced.cancel();
  const { rubberBand } = state();
  const points = getActiveRubberBandPoints();
  if (rubberBand.mode === 'draw') {
    populateWithPanels();
    dispatch(SHOW_CURSOR());
  }

  if (rubberBand.mode === 'remove') {
    if (points && points.length > 0) {
      collectPanelsInArea(points);
    }
    removePanels();
  }

  if (rubberBand.mode === 'exposure') {
    applyManualExposureFromRubberBand();
  }

  clearRubberBandPreview();
  dispatch(RESET_RUBBERBAND_STATE());
  dispatch(RESET_CURSOR_STATE());
  dispatch(SET_TOOLTIP_CONTENT(null));
}

export const drawRubberBand = rubberBand => rubberBandModel(rubberBand);

export const removeRubberBand = children => removeChild(children, 'rubberBand');

function getActiveRubberBandPoints() {
  const previewPoints = getPreviewPoints();
  if (previewPoints && previewPoints.length) {
    return previewPoints;
  }
  return state().rubberBand.points;
}

function getDrawingArea() {
  const { start } = state().rubberBand;
  const points = getActiveRubberBandPoints();
  const { x, y } = cursorPosition();
  const mouseMoveCords = {
    startX: start.x,
    startY: start.y,
    endX: x,
    endY: y,
  };
  const direction = get_drawing_direction(mouseMoveCords);
  return {
    direction,
    points,
  };
}

function collectPanelsInArea(points: {x:number, y:number}[]) {
  const { settings: { canvasCenter }, rubberBand: { mode } } = state();
  const oldPanelsToRemove = getPreviewPanelsToRemove();
  const rubberBandAreaPolygon = new Polygon(points);
  const areaMinMax = getPolyMinMaxCoords(rubberBandAreaPolygon);
  areaMinMax.minX -= canvasCenter.x;
  areaMinMax.minY -= canvasCenter.y;
  areaMinMax.maxX -= canvasCenter.x;
  areaMinMax.maxY -= canvasCenter.y;
  const collidedPanels = searchCollisionItems(areaMinMax);
  let newPanelsToRemove: any[] = collidedPanels;
  if (mode === 'exposure') {
    const panelById = new Map<number, panelInState>(state().panels.panels.map(p => [p.id, p]));
    newPanelsToRemove = collidedPanels.filter((value: any) => {
      const panel = panelById.get(value.id);
      return panel ? isPanelMajorityInsideArea(panel, areaMinMax) : false;
    });
  } else if (mode === 'remove') {
    const panelById = new Map<number, panelInState>(state().panels.panels.map(p => [p.id, p]));
    newPanelsToRemove = collidedPanels.filter((value: any) => {
      const panel = panelById.get(value.id);
      return panel ? isPanelIntersectingArea(panel, areaMinMax) : false;
    });
  }
  const panels = newPanelsToRemove.map(value => { return { x: value.maxX, y: value.maxY }; });

  if (newPanelsToRemove.length > 0) {
    const panelIdsToRemove = mode === 'remove'
      ? pairWithSiblings(newPanelsToRemove)
      : newPanelsToRemove.map((value: any) => value.id);
    updateTooltipData(panels);
    applyPreviewPanelSelection(mode, panelIdsToRemove);
    if (mode === 'remove') {
      setPreviewModuleCounts({
        active: true,
        shadowCount: 0,
        removeCount: panelIdsToRemove.length,
      });
    }
  } else if (oldPanelsToRemove.length !== 0) {
    updatePreviewTooltip(getStage(), null);
    applyPreviewPanelSelection(mode, []);
    if (mode === 'remove') {
      setPreviewModuleCounts({ active: true, shadowCount: 0, removeCount: 0 });
    }
  }
}

function isPanelMajorityInsideArea(
  panel: { x: number, y: number, width: number, height: number },
  area: { minX: number, minY: number, maxX: number, maxY: number },
) {
  const minX = panel.x - panel.width / 2;
  const maxX = panel.x + panel.width / 2;
  const minY = panel.y - panel.height / 2;
  const maxY = panel.y + panel.height / 2;
  const overlapX = Math.min(maxX, area.maxX) - Math.max(minX, area.minX);
  const overlapY = Math.min(maxY, area.maxY) - Math.max(minY, area.minY);
  return overlapX > panel.width * EXPOSURE_COVERAGE_THRESHOLD &&
    overlapY > panel.height * EXPOSURE_COVERAGE_THRESHOLD;
}

function isPanelIntersectingArea(
  panel: { x: number, y: number, width: number, height: number },
  area: { minX: number, minY: number, maxX: number, maxY: number },
) {
  const minX = panel.x - panel.width / 2;
  const maxX = panel.x + panel.width / 2;
  const minY = panel.y - panel.height / 2;
  const maxY = panel.y + panel.height / 2;
  return minX < area.maxX && maxX > area.minX && minY < area.maxY && maxY > area.minY;
}

export const getExposureRibbonPoints = (points: { x: number, y: number }[]) => {
  if (!points || points.length === 0) {
    return points;
  }
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const inset = getExposureSelectionArea({
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  });
  return [
    new PIXI.Point(inset.minX, inset.minY),
    new PIXI.Point(inset.minX, inset.maxY),
    new PIXI.Point(inset.maxX, inset.maxY),
    new PIXI.Point(inset.maxX, inset.minY),
  ];
};

function getExposureSelectionArea(area: { minX: number, minY: number, maxX: number, maxY: number }) {
  const { panels: { panels } } = state();
  const samplePanel = panels && panels.find(p => p.width && p.height);
  if (!samplePanel) {
    return area;
  }
  const colInset = (samplePanel.width / 2) * EXPOSURE_RIBBON_INSET_RATIO;
  const rowInset = (samplePanel.height / 2) * EXPOSURE_RIBBON_INSET_RATIO;
  const insetX = (area.maxX - area.minX) > colInset * 2 ? colInset : 0;
  const insetY = (area.maxY - area.minY) > rowInset * 2 ? rowInset : 0;
  return {
    minX: area.minX + insetX,
    maxX: area.maxX - insetX,
    minY: area.minY + insetY,
    maxY: area.maxY - insetY,
  };
}

function populateWithPanelsShadow() {
  const { direction, points } = getDrawingArea();
  if (!points || points.length === 0) {
    return;
  }
  const stage = getStage();
  const panels = draw_panels_on_area(direction, points, createPreviewShadowPanel as unknown as DrawPanelFunction, null) || [];
  setPreviewModuleCounts({ active: true, shadowCount: panels.length, removeCount: 0 });
  if (panels.length > 0) {
    updateTooltipData(panels as any);
  } else if (stage) {
    updatePreviewTooltip(stage, null);
  }
  if (stage) {
    syncShadowSprites(stage, panels);
    presentPreviewStage();
  }
}

const populateWithPanelsShadowDebounced = debounce(populateWithPanelsShadow, 16);

function createPreviewShadowPanel(x: number, y: number) {
  const { settings: { canvasCenter, panelWidth, panelHeight } } = state();
  const panelX = x - canvasCenter.x;
  const panelY = y - canvasCenter.y;
  return {
    x: panelX,
    y: panelY,
    width: panelWidth,
    height: panelHeight,
    id: previewShadowId(panelX, panelY),
  };
}

function populateWithPanels() {
  const { direction, points } = getDrawingArea();
  if (!points || points.length === 0) {
    return;
  }
  const groupId = getNearestGroup() || nextUid();
  const panels = draw_panels_on_area(direction, points, createPanel, groupId);
  dispatch(REQUEST_ADD_PANELS(panels));
}

function removePanels() {
  const currentState = state();
  const panelsToRemove = getPreviewPanelsToRemove().length
    ? getPreviewPanelsToRemove()
    : currentState.rubberBand.panelsToRemove;
  const { background: { moveArrayMode, toggleBaysContainer }, projectConfiguration: { productId } } = currentState;
  dispatch(REMOVE_PANELS(panelsToRemove, moveArrayMode, toggleBaysContainer, productId));
}

function applyManualExposureFromRubberBand() {
  const { rubberBand: { panelsToRemove: reduxPanelsToRemove }, background: { manualExposureKind } } = state();
  const panelsToRemove = getPreviewPanelsToRemove().length
    ? getPreviewPanelsToRemove()
    : reduxPanelsToRemove;
  if (!manualExposureKind || !panelsToRemove || panelsToRemove.length === 0) {
    return;
  }
  const kind: 'E' | 'NE' = manualExposureKind === 'expose' ? 'E' : 'NE';
  dispatch(SET_MANUAL_EXPOSURE_BULK(panelsToRemove as number[], kind));
}

function updateTooltipData(panels: Array<{ x: number; y: number }>) {
  const rowsAndColumns = panels.reduce((acc, panel) => {
    acc.rows.add(Math.floor(panel.y * 100) / 100);
    acc.columns.add(Math.floor(panel.x * 100) / 100);
    return acc;
  },                                   {
    rows: new Set([]),
    columns: new Set([]),
  });
  const content = `${rowsAndColumns.rows.size}x${rowsAndColumns.columns.size}`;
  const stage = getStage();
  if (stage) {
    updatePreviewTooltip(stage, content);
  }
}

function _calculatePoints(starX, startY, diagonalX, diagonalY) {
  const initialX = starX;
  const initialY = startY;
  const x2 = initialX;
  const y2 = diagonalY;

  const x4 = diagonalX;
  const y4 = initialY;

  return [
    new PIXI.Point(initialX, initialY),
    new PIXI.Point(x2, y2),
    new PIXI.Point(diagonalX, diagonalY),
    new PIXI.Point(x4, y4),
  ];
}

function draw_panels_on_area(direction: string, points: { x: number, y: number }[], createPanel: DrawPanelFunction, groupId: number | null) {
  const { projectConfiguration: { productId } } = state();

  if (isRMDT(productId)) {
    return draw_double_panels_on_area(direction, points, createPanel, groupId);
  }

  if (isGFT(productId)) {
    return drawGftPanelsInTables(direction, points, createPanel, groupId);
  }

  if (isAscender(productId)) {
    return drawAscenderPanelsInTables(direction, points, createPanel, groupId);
  }

  if(isULA(productId) ) {
    return drawUlaPanelsInTables(direction, points, createPanel, groupId);
  }
  
  return draw_on_area(direction, points, createPanel, groupId);
}
